/**
 * useNotifications — single source of truth for the notification UI.
 *
 * Primary delivery: AppSync subscription on `onNotification` (sub-second push).
 * Fallback: REST polling every 5 minutes — closes the gap if the WS drops
 * silently. The polling tick also runs immediately whenever the tab becomes
 * visible after being hidden, so badges are current as soon as the user comes
 * back to the browser.
 *
 * The hook's public API stays the same regardless of which channel delivered
 * the data — components don't need to know.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { generateClient } from "aws-amplify/api";

import {
  Notification,
  notificationService,
} from "../services/notification.service";
import { logger } from "../utils/logger";

// Fallback heartbeat — keeps the bell current when the AppSync WebSocket
// is unavailable (local dev) or drops silently. 15 seconds for local dev
// (no AppSync = must poll), 5 minutes for prod (AppSync is the primary).
const IS_APPSYNC_CONFIGURED = !!(
  process.env.REACT_APP_APPSYNC_GRAPHQL_URL ||
  process.env.REACT_APP_APPSYNC_API_KEY
);
const POLL_INTERVAL_MS = IS_APPSYNC_CONFIGURED ? 300_000 : 15_000;

// GraphQL subscription document. Inlined (rather than imported from a
// codegen file) because the AppSync schema is small and we don't yet run
// graphql-codegen in this repo.
const ON_NOTIFICATION_SUBSCRIPTION = /* GraphQL */ `
  subscription OnNotification {
    onNotification {
      notificationId
      userId
      clinicId
      eventType
      title
      body
      link
      payload
      createdAt
    }
  }
`;

function safeParseJson(s: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(s);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

// Shape of the subscription event payload — matches the GraphQL Notification
// type. AppSync returns dates as ISO strings; we don't transform them here
// because the rest of the hook also works in ISO.
interface OnNotificationEvent {
  data?: {
    onNotification?: {
      notificationId: string;
      userId: string | null;
      clinicId: string;
      eventType: string;
      title: string;
      body: string;
      link: string | null;
      payload: string | Record<string, unknown>;   // AWSJSON arrives as a string
      createdAt: string;
    };
  };
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  markRead: (notificationId: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  dismiss: (notificationId: string) => Promise<void>;
  dismissAll: () => Promise<void>;
}

export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Refs so callbacks never go stale without appearing in dep arrays.
  const mountedRef = useRef(true);
  const nextCursorRef = useRef<string | null>(null);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [listResp, countResp] = await Promise.all([
        notificationService.list({ limit: 20, is_dismissed: false }),
        notificationService.unreadCount(),
      ]);
      if (!mountedRef.current) return;
      setNotifications(listResp.items);
      setUnreadCount(countResp.count);
      nextCursorRef.current = listResp.next_cursor ?? null;
      setHasMore(listResp.next_cursor != null);
      setError(null);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!nextCursorRef.current || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const resp = await notificationService.list({
        limit: 20,
        is_dismissed: false,
        cursor: nextCursorRef.current,
      });
      if (!mountedRef.current) return;
      setNotifications(prev => {
        const seen = new Set(prev.map(n => n.notification_id));
        return [...prev, ...resp.items.filter(n => !seen.has(n.notification_id))];
      });
      nextCursorRef.current = resp.next_cursor ?? null;
      setHasMore(resp.next_cursor != null);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      loadingMoreRef.current = false;
      if (mountedRef.current) setLoadingMore(false);
    }
  }, []);

  // Primary delivery: AppSync subscription. Fallback: REST polling every 5
  // minutes (heartbeat in case the WS drops silently). On tab-visibility
  // change to visible, we always refresh so the badge is current immediately.
  useEffect(() => {
    void refresh();

    // ── AppSync subscription ──────────────────────────────────────────────
    // generateClient throws or fails to connect when AppSync isn't configured
    // (e.g., before CDK deploy or if REACT_APP_APPSYNC_GRAPHQL_URL is empty).
    // We catch that and continue with polling only.
    let subscription: { unsubscribe: () => void } | null = null;
    try {
      const client = generateClient();
      // The `any` cast on .subscribe is needed because aws-amplify's GraphQL
      // typings are heavily generic; we use a runtime check inside the
      // callback rather than fight the types.
      const result = client.graphql({ query: ON_NOTIFICATION_SUBSCRIPTION });
      if (result && typeof (result as any).subscribe === "function") {
        subscription = (result as any).subscribe({
          next: (event: OnNotificationEvent) => {
            const incoming = event?.data?.onNotification;
            if (!incoming || !mountedRef.current) return;
            // Parse the AWSJSON payload (arrives as a string from AppSync).
            const payload: Record<string, unknown> =
              typeof incoming.payload === "string"
                ? safeParseJson(incoming.payload)
                : incoming.payload;

            const next: Notification = {
              notification_id: incoming.notificationId,
              user_id: incoming.userId,
              clinic_id: incoming.clinicId,
              event_type: incoming.eventType,
              title: incoming.title,
              body: incoming.body,
              link: incoming.link,
              payload,
              is_read: false,
              is_dismissed: false,
              created_at: incoming.createdAt,
              read_at: null,
            };

            // Dedupe — a notification could arrive via subscription AND a
            // catch-up REST refresh that happened to fire at the same time.
            setNotifications(prev =>
              prev.some(n => n.notification_id === next.notification_id)
                ? prev
                : [next, ...prev],
            );
            setUnreadCount(prev => prev + 1);

            // Trigger feature flag refetch on subscription events
            if (incoming.eventType === "subscription_activated") {
              window.dispatchEvent(new CustomEvent("billing:subscription-changed"));
            }
          },
          error: (err: unknown) => {
            // Falls back to 5-min polling. If this fires, check
            // REACT_APP_APPSYNC_GRAPHQL_URL first (amplifyConfig.ts).
            logger.warn("AppSync onNotification subscription failed", {
              error: err instanceof Error ? err.message : String(err),
            });
          },
        });
      }
    } catch (err) {
      logger.warn("AppSync subscription unavailable; falling back to polling only", {
        error: err instanceof Error ? err.message : String(err),
      });
    }

    // ── Polling heartbeat (only ticks when tab is visible) ────────────────
    const tick = () => {
      if (document.visibilityState === "visible") {
        void refresh();
      }
    };
    const interval = setInterval(tick, POLL_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      subscription?.unsubscribe();
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refresh]);

  // ── Actions ─────────────────────────────────────────────────────────────
  // Each action does an optimistic update (so the UI feels instant) and
  // then triggers a refresh to reconcile with server state. If the server
  // call fails, the next refresh will revert any incorrect optimistic state.

  const markRead = useCallback(async (notificationId: string) => {
    setNotifications(prev =>
      prev.map(n =>
        n.notification_id === notificationId ? { ...n, is_read: true } : n,
      ),
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await notificationService.markRead(notificationId);
    } finally {
      await refresh();
    }
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
    try {
      await notificationService.markAllRead();
    } finally {
      await refresh();
    }
  }, [refresh]);

  const dismiss = useCallback(async (notificationId: string) => {
    setNotifications(prev =>
      prev.filter(n => n.notification_id !== notificationId),
    );
    try {
      await notificationService.dismiss(notificationId);
    } finally {
      await refresh();
    }
  }, [refresh]);

  const dismissAll = useCallback(async () => {
    setNotifications([]);
    setUnreadCount(0);
    try {
      await notificationService.dismissAll();
    } finally {
      await refresh();
    }
  }, [refresh]);

  return {
    notifications,
    unreadCount,
    loading,
    loadingMore,
    hasMore,
    error,
    refresh,
    loadMore,
    markRead,
    markAllRead,
    dismiss,
    dismissAll,
  };
}
